<!-- ============================================================
 * MaintenanceNotice.vue  ⚠️ 임시(배포/테스트용) — 종료 후 삭제 대상
 *
 * 문의하기 운영서버 직접 배포·테스트 구간 동안, /:lang/contact-us 진입
 * 사용자에게 점검 안내 + 대체 연락 수단(이메일/전화)을 안내한다.
 *
 * ▣ 제거 방법 (테스트 종료 후):
 *   1) 이 파일 삭제
 *   2) ContactPage.vue 에서 import / components 등록 / <MaintenanceNotice /> 3곳 제거
 *
 * ▣ 동작:
 *   • 비차단(안내만) — 닫으면 폼 정상 사용
 *   • 페이지 진입 시마다 표시 (종료 시각 NOTICE_UNTIL 까지)
 *   • NOTICE_UNTIL 시각이 지나면 코드가 남아 있어도 자동으로 표시되지 않음
 *
 * ▣ 수정 포인트: 아래 상수(NOTICE_*, CONTACT_PHONE)만 고치면 됨
 * ============================================================ -->
<template>
    <v-dialog
        v-model="visible"
        width="480"
    >
        <div class="mn-card">
            <!-- 제목 -->
            <h3 class="mn-title">
                {{ text.title }}
            </h3>

            <!-- 본문 -->
            <p class="mn-lead">
                {{ text.intro }}
            </p>

            <!-- 핵심: 이용 가능 시점 (강조 박스) -->
            <div class="mn-when">
                {{ text.when }}
            </div>

            <p class="mn-help">
                {{ text.help }}
            </p>

            <!-- 대체 연락처 박스 -->
            <div class="mn-contact">
                <div
                    v-if="contactPhone"
                    class="mn-contact__row"
                >
                    <span class="mn-contact__label">{{ text.phoneLabel }}</span>
                    <span class="mn-contact__value">{{ contactPhone }}</span>
                </div>
                <div class="mn-contact__row">
                    <span class="mn-contact__label">{{ text.emailLabel }}</span>
                    <a
                        :href="`mailto:${contactEmail}`"
                        class="mn-contact__link"
                    >{{ contactEmail }}</a>
                </div>
            </div>

            <p class="mn-apology">
                {{ text.apology }}
            </p>

            <!-- 닫기 -->
            <div class="mn-foot">
                <button
                    type="button"
                    class="mn-btn"
                    @click="close"
                >
                    {{ text.button }}
                </button>
            </div>
        </div>
    </v-dialog>
</template>

<script>
// ─── 임시 설정 상수 (여기만 수정) ───────────────────────────
// 이 시각 이후 자동 미표시. 'YYYY-MM-DDTHH:mm' (사용자 로컬=KST 기준). 비우면 항상 비표시.
const NOTICE_UNTIL = '2026-06-29T14:00';

// 마스터 토글 — false 로 두면 즉시 비표시
const NOTICE_ENABLED = true;

// 대체 연락 — 전화번호 채우면 전화 줄 노출, 비우면 미표시 / 이메일은 항상 노출
const CONTACT_PHONE = '';                       // 예: '02-1234-5678'
const CONTACT_EMAIL = 'songmi.goh@nl-hd.com';

// 안내 문구 (한/영) — 자유롭게 수정. when 의 시각은 NOTICE_UNTIL 과 함께 맞춰 주세요.
const NOTICE_TEXT = {
    ko: {
        title: '문의 기능 점검 안내',
        intro: '현재 문의하기 기능을 업데이트하고 있어, 일시적으로 오류가 발생할 수 있습니다.',
        when: '6월 29일(월) 오후 2시 이후, 또는 본 안내가 사라진 뒤 이용해 주세요.',
        help: '급하신 경우 아래로 문의해 주시면 신속히 도와드리겠습니다.',
        phoneLabel: '전화',
        emailLabel: '이메일',
        apology: '불편을 드려 죄송합니다. — 레코스 드림',
        button: '확인',
    },
    en: {
        title: 'Temporary Maintenance Notice',
        intro: 'We are currently updating our inquiry feature, so you may experience temporary errors.',
        when: 'Please try again after 2 PM (KST) on Mon, June 29, or once this notice disappears.',
        help: 'If your inquiry is urgent, please reach us below and we will help right away.',
        phoneLabel: 'Phone',
        emailLabel: 'Email',
        apology: 'We apologize for any inconvenience. — Team LecoS',
        button: 'OK',
    },
};

export default {
    name: 'MaintenanceNotice',

    data() {
        return {
            visible: false,
            contactPhone: CONTACT_PHONE,
            contactEmail: CONTACT_EMAIL,
        };
    },

    computed: {
        /** 라우트 :lang 파라미터로 한/영 분기 (eng 외에는 한국어) */
        text() {
            const lang = this.$route && this.$route.params && this.$route.params.lang;
            return lang === 'eng' ? NOTICE_TEXT.en : NOTICE_TEXT.ko;
        },
    },

    mounted() {
        this.maybeShow();
    },

    methods: {
        /** 활성·미만료면 페이지 진입 시마다 표시 (종료 시각까지 매번) */
        maybeShow() {
            if (!NOTICE_ENABLED) return;

            const until = new Date(NOTICE_UNTIL);
            // 유효하지 않거나(빈 값 등) 종료 시각이 지났으면 표시하지 않음
            if (isNaN(until.getTime()) || Date.now() >= until.getTime()) return;

            this.visible = true;
        },

        close() {
            this.visible = false;
        },
    },
};
</script>

<style lang="scss" scoped>
// 자기완결형 스타일(토큰 미사용) — 제거 시 파일만 지우면 됨
$mn-orange    : #FF8228;
$mn-orange-dk : #c2410c;
$mn-text      : #1f2937;
$mn-text-sub  : #4b5563;
$mn-muted     : #9ca3af;

.mn-card {
    padding:       28px 28px 22px;
    background:    #fff;
    border-radius: 14px;
}

.mn-title {
    margin:         0 0 14px;
    font-size:      2.1rem;
    font-weight:    800;
    line-height:    1.35;
    letter-spacing: -0.02em;
    color:          $mn-text !important;   // 전역 h 태그 color 오버라이드
}

.mn-lead,
.mn-help {
    margin:      0 0 12px;
    font-size:   1.5rem;
    line-height: 1.72;
    color:       $mn-text-sub;
}

// 핵심 시점 — 박스 없이 굵은 한 줄로만 강조
.mn-when {
    margin:      0 0 12px;
    font-size:   1.5rem;
    font-weight: 700;
    line-height: 1.72;
    color:       $mn-text;
}

// 대체 연락처 박스
.mn-contact {
    margin:        4px 0 16px;
    padding:       12px 16px;
    border-radius: 10px;
    background:    #f6f7f9;
}

.mn-contact__row {
    display:     flex;
    align-items: center;
    gap:         10px;
    padding:     3px 0;
    font-size:   1.45rem;
    line-height: 1.5;
}

.mn-contact__label {
    flex-shrink: 0;
    min-width:   48px;
    font-weight: 700;
    color:       #6b7280;
}

.mn-contact__value { color: $mn-text; }

.mn-contact__link {
    font-weight:     700;
    color:           $mn-orange;
    text-decoration: none;
    word-break:      break-all;

    &:hover { text-decoration: underline; }
}

.mn-apology {
    margin:    0;
    font-size: 1.3rem;
    color:     $mn-muted;
}

.mn-foot {
    margin-top:      20px;
    display:         flex;
    justify-content: flex-end;
}

.mn-btn {
    height:        40px;
    padding:       0 22px;
    border:        none;
    border-radius: 8px;
    background:    $mn-orange;
    color:         #fff;
    font-size:     1.45rem;
    font-weight:   700;
    cursor:        pointer;
    transition:    background .15s ease;

    &:hover { background: #e67320; }
}

@media (max-width: 480px) {
    .mn-card  { padding: 22px 20px 18px; }
    .mn-title { font-size: 1.9rem; }
    .mn-lead, .mn-help, .mn-when { font-size: 1.4rem; }
}
</style>
